import React from 'react';
import PropTypes from 'prop-types';
import { SYRINGE_SPECS } from '../constants/syringeTypes';
import './SyringeDisplay.css';

function SyringeDisplay({ fillLevel, syringeType, currentDose }) {
  const spec = SYRINGE_SPECS[syringeType];
  const majorTicks = syringeType === '3ml' ? 6 : 10;

  const generateTicks = () => {
    const ticks = [];
    for (let i = 0; i <= majorTicks; i++) {
      const percentage = (i / majorTicks) * 100;
      const units = Math.round(spec.units - (i / majorTicks) * spec.units);
      ticks.push(
        <div key={i} className="tick-mark" style={{ top: `${percentage}%` }}>
          <div className="tick-label">{units}</div>
          <div className="tick-line"></div>
        </div>
      );
    }
    return ticks;
  };

  return (
    <div className="syringe-display">
      <h3 className="syringe-title">Syringe Visualization</h3>

      <div className="syringe-container">
        <div className="syringe-wrapper">
          <svg
            width={spec.width + 40}
            height={spec.height + 100}
            viewBox={`0 0 ${spec.width + 40} ${spec.height + 100}`}
            className="syringe-svg"
          >
            <defs>
              <linearGradient id="barrelGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(255, 255, 255, 0.3)" />
                <stop offset="50%" stopColor="rgba(255, 255, 255, 0.1)" />
                <stop offset="100%" stopColor="rgba(255, 255, 255, 0.3)" />
              </linearGradient>

              <linearGradient id="fluidGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#667eea" />
                <stop offset="100%" stopColor="#764ba2" />
              </linearGradient>

              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Plunger */}
            <rect
              x={(spec.width + 40) / 2 - 15}
              y="10"
              width="30"
              height="15"
              fill="rgba(255, 255, 255, 0.8)"
              rx="3"
            />

            {/* Plunger rod */}
            <rect
              x={(spec.width + 40) / 2 - 3}
              y="25"
              width="6"
              height={30 + (spec.height - 60) * (1 - fillLevel / 100)}
              fill="rgba(255, 255, 255, 0.6)"
            />

            {/* Barrel */}
            <rect
              x="20"
              y="60"
              width={spec.width}
              height={spec.height - 60}
              fill="url(#barrelGradient)"
              stroke="rgba(255, 255, 255, 0.5)"
              strokeWidth="2"
              rx="5"
            />

            {/* Fluid */}
            {fillLevel > 0 && (
              <rect
                x="22"
                y={60 + (spec.height - 60) * (1 - fillLevel / 100)}
                width={spec.width - 4}
                height={(spec.height - 60) * (fillLevel / 100) - 2}
                fill="url(#fluidGradient)"
                filter="url(#glow)"
                opacity="0.8"
                rx="3"
              />
            )}

            {/* Needle hub */}
            <polygon
              points={`${(spec.width + 40) / 2 - 10},${spec.height} ${(spec.width + 40) / 2 + 10},${spec.height} ${(spec.width + 40) / 2 + 5},${spec.height + 20} ${(spec.width + 40) / 2 - 5},${spec.height + 20}`}
              fill="rgba(255, 255, 255, 0.7)"
              stroke="rgba(255, 255, 255, 0.5)"
              strokeWidth="1"
            />

            {/* Needle */}
            <rect
              x={(spec.width + 40) / 2 - 1}
              y={spec.height + 20}
              width="2"
              height="40"
              fill="rgba(255, 255, 255, 0.9)"
            />

            {/* Needle tip */}
            <polygon
              points={`${(spec.width + 40) / 2 - 1},${spec.height + 60} ${(spec.width + 40) / 2 + 1},${spec.height + 60} ${(spec.width + 40) / 2},${spec.height + 65}`}
              fill="rgba(255, 255, 255, 0.9)"
            />
          </svg>

          <div className="tick-marks">{generateTicks()}</div>
        </div>

        <div className="syringe-info">
          <div className="info-item">
            <span className="info-label">Type:</span>
            <span className="info-value">{syringeType.toUpperCase()}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Capacity:</span>
            <span className="info-value">{spec.units} units</span>
          </div>
          <div className="info-item">
            <span className="info-label">Fill Level:</span>
            <span className="info-value">{fillLevel.toFixed(1)}%</span>
          </div>

          {currentDose && (
            <div className="dose-info">
              <h4>Current Dose:</h4>
              <div className="dose-details">
                <div>{currentDose.dose} mg</div>
                <div>{currentDose.volume} mL</div>
                <div>{currentDose.units} units</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="syringe-note">
        The syringe fills dynamically based on your dosage calculation
      </div>
    </div>
  );
}

SyringeDisplay.propTypes = {
  fillLevel: PropTypes.number.isRequired,
  syringeType: PropTypes.string.isRequired,
  currentDose: PropTypes.shape({
    dose: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    volume: PropTypes.string,
    units: PropTypes.string,
  }),
};

SyringeDisplay.defaultProps = {
  currentDose: null,
};

export default SyringeDisplay;
